import { useState, useEffect } from 'react'
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Divider,
  Snackbar,
  Alert,
  Card,
  FormControl,
  FormLabel,
  IconButton,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import MinimizeIcon from '@mui/icons-material/Minimize'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { GridBackground } from './GridBackground'
import { BouncingPuzzleIcons } from './BouncingPuzzleIcons'
import { GridOverlay } from './GridOverlay'

export function LoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [notAuthorizedOpen, setNotAuthorizedOpen] = useState(false)
  const [requestAccessToken, setRequestAccessToken] = useState('')
  // Shown when OneLogin did not grant the app to the user (no OIDC code in callback):
  // the user must request the OneLogin entitlement from the DPG IT Service Desk.
  const [noOneLoginAccessOpen, setNoOneLoginAccessOpen] = useState(false)
  // Username/password login is hidden by default; only shown with ?allowUsernamePasswordLogin=true.
  const [allowUsernamePasswordLogin] = useState(
    () => new URLSearchParams(window.location.search).get('allowUsernamePasswordLogin') === 'true'
  )
  const [accessRequestState, setAccessRequestState] = useState<'idle' | 'sending' | 'sent' | 'error'>(
    'idle'
  )
  const [isMinimized, setIsMinimized] = useState(false)

  // OneLogin entitlement that grants access to the GMSv3 Prod admin panel.
  const oneLoginEntitlement = 'APP_1L_Access-GamesBondAdminPanel-Prod-Users'

  // Prefilled mail to the DPG IT Service Desk requesting the GMSv3 OneLogin entitlement.
  const serviceDeskMailto =
    'mailto:helpdesk@persgroep.be' +
    `?subject=${encodeURIComponent(`Toegang tot GMSv3 - ${oneLoginEntitlement}`)}` +
    `&body=${encodeURIComponent(
      'Beste ServiceDesk, Via deze route vraag ik toegang tot de OneLogin entitlement: ' +
        `${oneLoginEntitlement} (GMSv3 Prod, applicatie id: 471631).`
    )}`

  useEffect(() => {
    // Check for error message in URL query params
    const urlParams = new URLSearchParams(window.location.search)
    const messageParam = urlParams.get('message')

    if (messageParam === 'Not authorized') {
      // OIDC login succeeded (the user HAS OneLogin access) but the account is not yet
      // onboarded in the admin: only ask them to request onboarding via the games team.
      // A one-time token (bound to the email server-side) authorizes the access request.
      setRequestAccessToken(urlParams.get('request_access_token') || '')
      setNotAuthorizedOpen(true)
    } else if (messageParam === 'Missing OIDC Code in callback') {
      // OneLogin returned no code: the user lacks the OneLogin entitlement for this app,
      // so the only valid path is to request that entitlement from the Service Desk.
      setNoOneLoginAccessOpen(true)
    } else if (messageParam) {
      setSnackbarMessage(messageParam)
      setSnackbarOpen(true)
    }
  }, [])

  const handleCloseNotAuthorized = () => {
    setNotAuthorizedOpen(false)
  }

  const handleCloseNoOneLoginAccess = () => {
    setNoOneLoginAccessOpen(false)
  }

  const handleRequestAccess = async () => {
    setAccessRequestState('sending')
    try {
      const response = await fetch(
        `/oidc/request-access?request_access_token=${encodeURIComponent(requestAccessToken)}`,
        { method: 'POST' }
      )
      setAccessRequestState(response.ok ? 'sent' : 'error')
    } catch {
      setAccessRequestState('error')
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const form = e.target as HTMLFormElement
    form.submit()
  }

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  const handleMinimize = () => {
    setIsMinimized(true)
  }

  const handleRestore = () => {
    setIsMinimized(false)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: (theme) =>
          theme.palette.mode === 'light'
            ? 'linear-gradient(180deg, #FFF 50%, hsl(210, 100%, 97%) 100%)'
            : 'linear-gradient(180deg, hsl(210, 14%, 7%) 50%, hsl(210, 100%, 16%) 100%)',
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <GridBackground />
      <BouncingPuzzleIcons />
      <GridOverlay />

      {/* Minimized Icon */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: 80,
          zIndex: 100,
        }}
      >
        <Zoom in={isMinimized} timeout={500}>
          <IconButton
            onClick={handleRestore}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <AccountCircleIcon sx={{ color: 'white' }} />
          </IconButton>
        </Zoom>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          top: isMinimized ? '36px' : '50%',
          left: isMinimized ? 'calc(100vw - 100px)' : '50%',
          transform: isMinimized
            ? 'translate(-50%, -50%) scale(0.05)'
            : 'translate(-50%, -50%) scale(1)',
          transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
          opacity: isMinimized ? 0 : 1,
          pointerEvents: isMinimized ? 'none' : 'auto',
          zIndex: 20,
          width: '100%',
          maxWidth: '444px',
        }}
      >
        <Container maxWidth="xs" disableGutters>
        <Card
          elevation={0}
          sx={{
            p: 4,
            boxShadow: (theme) =>
              theme.palette.mode === 'light'
                ? '0 0 1px rgba(85, 166, 246, 0.1), 1px 1.5px 2px -1px rgba(85, 166, 246, 0.15), 4px 4px 12px -2.5px rgba(85, 166, 246, 0.15)'
                : '0 0 1px rgba(2, 31, 59, 0.7), 1px 1.5px 2px -1px rgba(2, 31, 59, 0.65), 4px 4px 12px -2.5px rgba(2, 31, 59, 0.65)',
          }}
        >
          {/* Minimize Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -2, mr: -2 }}>
            <IconButton
              onClick={handleMinimize}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                },
              }}
            >
              <MinimizeIcon />
            </IconButton>
          </Box>
          {/* Logo/Icon */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                backgroundColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <LockIcon sx={{ fontSize: 24 }} />
            </Box>
          </Box>

          {/* Title */}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 600,
              mb: 4,
              textAlign: 'center',
            }}
          >
            Sign in
          </Typography>

          {/* Username/Password Form */}
          <Box
            component="form"
            action="/session/login"
            method="post"
            onSubmit={handleSubmit}
            noValidate
          >
            {allowUsernamePasswordLogin && (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <FormLabel htmlFor="username" sx={{ mb: 1, fontWeight: 500 }}>
                    Username
                  </FormLabel>
                  <TextField
                    fullWidth
                    id="username"
                    name="username"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <FormLabel htmlFor="password" sx={{ mb: 1, fontWeight: 500 }}>
                    Password
                  </FormLabel>
                  <TextField
                    fullWidth
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </FormControl>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mb: 3, py: 1.5 }}
                >
                  Sign in
                </Button>

                {/* Divider */}
                <Divider sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', px: 1 }}>
                    or
                  </Typography>
                </Divider>
              </>
            )}

            {/* OneLogin Button */}
            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<VpnKeyIcon />}
              href="/oidc/login"
              sx={{ py: 1.5 }}
            >
              Sign in with OneLogin
            </Button>
          </Box>
        </Card>
        </Container>
      </Box>

      {/* Snackbar for errors */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error">
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Modal shown when OneLogin auth succeeded but the account is not onboarded yet.
          The user already has OneLogin access, so we only ask them to request onboarding. */}
      <Dialog open={notAuthorizedOpen} onClose={handleCloseNotAuthorized} maxWidth="sm" fullWidth>
        <DialogTitle>Almost there</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your OneLogin sign-in succeeded, but your account is not yet onboarded on this system.
            Request access below and the games team will onboard you.
          </DialogContentText>

          {/* Notify the games team (fires the Slack webhook server-side) */}
          <Button
            onClick={handleRequestAccess}
            variant="contained"
            size="large"
            fullWidth
            disabled={accessRequestState === 'sending' || accessRequestState === 'sent'}
            sx={{ mt: 3, py: 1.5, fontSize: '1rem' }}
          >
            {accessRequestState === 'sending'
              ? 'Sending request…'
              : accessRequestState === 'sent'
                ? 'Access request sent ✓'
                : 'Request access to GMSv3'}
          </Button>

          {accessRequestState === 'sent' && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Your access request was sent. The games team will get back to you.
            </Alert>
          )}
          {accessRequestState === 'error' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Could not send the request. Please reach out in{' '}
              <Link
                href="https://persgroep-it.slack.com/archives/C065QRH0TPW"
                target="_blank"
                rel="noopener noreferrer"
              >
                #games-content
              </Link>{' '}
              directly.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotAuthorized}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Modal shown when OneLogin did not grant the app (no OIDC code in callback).
          The user lacks the OneLogin entitlement and must request it from the Service Desk. */}
      <Dialog
        open={noOneLoginAccessOpen}
        onClose={handleCloseNoOneLoginAccess}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>No OneLogin access</DialogTitle>
        <DialogContent>
          <DialogContentText>
            OneLogin did not grant you access to this application. Request the OneLogin entitlement{' '}
            <strong>{oneLoginEntitlement}</strong> from the DPG IT Service Desk. Once granted, sign
            in again.
          </DialogContentText>

          <Button
            component="a"
            href={serviceDeskMailto}
            variant="contained"
            size="large"
            fullWidth
            sx={{ mt: 3, py: 1.5 }}
          >
            Email DPG IT Service Desk
          </Button>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Or email <Link href={serviceDeskMailto}>helpdesk@persgroep.be</Link> and ask for the
            entitlement <strong>{oneLoginEntitlement}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNoOneLoginAccess}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
